import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Trash2, Pencil, Link2, Upload as UploadIcon } from "lucide-react";
import { Drawer, DrawerActions, Grid2, F } from "@/components/admin/CMS";
import { RESOURCE_TYPES, downloadUrl } from "@/lib/resources";

const fmtSize = (b) => (b ? `${(b / 1024 / 1024).toFixed(2)} MB` : "");

export default function ResourcesCMS() {
  const [items, setItems] = useState([]);
  const [episodes, setEpisodes] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [r, e] = await Promise.all([api.get("/admin/recursos"), api.get("/admin/episodes")]);
    setItems(r.data); setEpisodes(e.data);
  };
  useEffect(() => { load(); }, []);

  const blank = () => ({ title: "", description: "", resource_type: "Plantilla Excel", guest_name: "", episode_id: "", is_published: false, order: items.length + 1 });

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este recurso? El enlace de descarga dejará de funcionar.")) return;
    await api.delete(`/admin/recursos/${id}`);
    toast.success("Eliminado"); load();
  };

  const togglePublish = async (r) => {
    await api.put(`/admin/recursos/${r.id}`, { ...r, is_published: !r.is_published });
    load();
  };

  const copyLink = async (id) => {
    try { await navigator.clipboard.writeText(downloadUrl(id)); toast.success("Enlace copiado"); }
    catch { window.prompt("Copia el enlace:", downloadUrl(id)); }
  };

  return (
    <div data-testid="cms-resources">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-white">Libros y Recursos</h2>
        <button data-testid="cms-resource-new" onClick={() => setEditing(blank())} className="mp-btn-primary !py-2 !px-4 !text-[11px]"><Plus size={14}/> Nuevo</button>
      </div>
      <div className="border border-white/10">
        {items.length === 0 && <div className="p-8 text-mp-mute text-sm">Sin recursos aún.</div>}
        {items.map((r) => (
          <ResourceRow key={r.id} r={r} onEdit={() => setEditing(r)} onDelete={() => del(r.id)} onToggle={() => togglePublish(r)} onCopy={() => copyLink(r.id)} />
        ))}
      </div>
      {editing && <ResourceEditor initial={editing} episodes={episodes} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function ResourceRow({ r, onEdit, onDelete, onToggle, onCopy }) {
  return (
    <div data-testid={`cms-resource-row-${r.id}`} className="flex flex-wrap items-center gap-4 px-4 py-4 border-b border-white/5">
      <div className="flex-1 min-w-[220px]">
        <div className="text-white font-display">{r.title}</div>
        <div className="text-mp-mute text-xs mt-1">{r.resource_type}{r.guest_name ? ` · ${r.guest_name}` : ""} · {r.file_name || "Sin archivo"} {fmtSize(r.size)} · {r.downloads || 0} descargas</div>
      </div>
      <button data-testid={`cms-resource-toggle-${r.id}`} onClick={onToggle} className={`px-2 py-1 text-[10px] tracking-[0.2em] uppercase font-display ${r.is_published ? "bg-mp-orange/20 text-mp-orange" : "border border-white/15 text-white/60"}`}>
        {r.is_published ? "Publicado" : "Borrador"}
      </button>
      <div className="flex items-center gap-3">
        <button data-testid={`cms-resource-copy-${r.id}`} onClick={onCopy} title="Copiar enlace de descarga" className="text-white/60 hover:text-mp-orange"><Link2 size={15} /></button>
        <button data-testid={`cms-resource-edit-${r.id}`} onClick={onEdit} className="text-white/60 hover:text-mp-orange"><Pencil size={14} /></button>
        <button data-testid={`cms-resource-del-${r.id}`} onClick={onDelete} className="text-white/60 hover:text-red-400"><Trash2 size={14} /></button>
      </div>
    </div>
  );
}

function ResourceEditor({ initial, episodes, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    if (!form.title.trim()) { toast.error("El título es obligatorio"); return; }
    if (!form.id && !file) { toast.error("Selecciona un archivo"); return; }
    setSaving(true);
    try {
      const r = form.id ? await api.put(`/admin/recursos/${form.id}`, form) : await api.post("/admin/recursos", form);
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        try { await api.post(`/admin/recursos/${r.data.id}/archivo`, fd); }
        catch (err) { if (!form.id) await api.delete(`/admin/recursos/${r.data.id}`); throw err; }
      }
      toast.success("Recurso guardado"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Drawer title={form.id ? "Editar recurso" : "Nuevo recurso"} onClose={onClose}>
      <Grid2>
        <F label="Título *" span={2}><input data-testid="resource-title-input" className="mp-input" value={form.title} onChange={(e) => set("title", e.target.value)} /></F>
        <F label="Descripción" span={2}><textarea data-testid="resource-description-input" className="mp-input" rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
        <F label="Tipo"><select data-testid="resource-type-select" className="mp-input bg-mp-black" value={form.resource_type} onChange={(e) => set("resource_type", e.target.value)}>{RESOURCE_TYPES.map((t) => <option key={t}>{t}</option>)}</select></F>
        <F label="Invitado relacionado"><input data-testid="resource-guest-input" className="mp-input" value={form.guest_name} onChange={(e) => set("guest_name", e.target.value)} /></F>
        <F label="Episodio relacionado">
          <select data-testid="resource-episode-select" className="mp-input bg-mp-black" value={form.episode_id || ""} onChange={(e) => set("episode_id", e.target.value)}>
            <option value="">Sin vincular</option>
            {episodes.map((e) => <option key={e.id} value={e.id}>{e.title || e.guest_name}</option>)}
          </select>
        </F>
        <F label="Orden"><input type="number" className="mp-input" value={form.order} onChange={(e) => set("order", parseInt(e.target.value || "0"))} /></F>
        <F label={form.id ? `Reemplazar archivo (actual: ${form.file_name || "ninguno"})` : "Archivo *"} span={2}>
          <label className="flex items-center gap-3 border border-dashed border-white/20 p-4 cursor-pointer hover:border-mp-orange/60 transition-colors">
            <UploadIcon size={16} className="text-mp-orange" />
            <span className="text-white/80 text-sm truncate">{file ? `${file.name} (${fmtSize(file.size)})` : "Seleccionar archivo (Excel, PDF, ebook…) · máx 100MB"}</span>
            <input data-testid="resource-file-input" type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </label>
        </F>
        <F label="Estado" span={2}>
          <label className="flex items-center gap-3 pt-2 cursor-pointer">
            <input data-testid="resource-published-checkbox" type="checkbox" checked={form.is_published} onChange={(e) => set("is_published", e.target.checked)} className="accent-mp-orange w-4 h-4" />
            <span className="text-white/80 text-sm">Publicado (visible en /libros)</span>
          </label>
        </F>
      </Grid2>
      <DrawerActions saving={saving} onSave={save} onCancel={onClose} testid="cms-resource-save" />
    </Drawer>
  );
}
